import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { Cog6ToothIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ConfiguracaoTermoService } from '@/modules/configuracaoTermo/services/configuracaoTermoService';
import { imagemService } from '@/services/imagemService';

const configuracaoTermoSchema = z.object({
    responsavelNome: z.string().optional(),
    responsavelRg: z.string().optional(),
    responsavelCargo: z.string().optional(),
    responsavelCpf: z.string().optional(),
    responsavelCnpj: z.string().optional(),
    rodapeEndereco: z.string().optional(),
    rodapeBairro: z.string().optional(),
    rodapeCidade: z.string().optional(),
    rodapeUf: z.string().optional(),
    rodapeCnpj: z.string().optional(),
    rodapeCep: z.string().optional(),
    rodapeTelefone: z.string().optional(),
    rodapeEmail: z.string().email('E-mail inválido').optional().or(z.literal('')),
    textoPersonalizado: z.string().optional(),
    imagemCabecalho: z.string().optional(),
});

type ConfiguracaoTermoFormData = z.infer<typeof configuracaoTermoSchema>;

const configuracaoTermoService = new ConfiguracaoTermoService();

// Lista de todos os estados brasileiros
const estadosBrasileiros = [
    { sigla: 'AC', nome: 'Acre' },
    { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'BA', nome: 'Bahia' },
    { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' },
    { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' },
    { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' },
    { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' },
    { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' },
    { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' },
    { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' },
    { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' }
];

export const ConfiguracaoTermoPage: React.FC = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { handleError, handleSuccess } = useErrorHandler();
    const [tipoDocumento, setTipoDocumento] = useState<'rg' | 'cpf' | 'cnpj'>('rg');
    const [documentoValor, setDocumentoValor] = useState<string>('');
    const [imagemCabecalho, setImagemCabecalho] = useState<string>('');
    const [imagemPreview, setImagemPreview] = useState<string>('');
    const [isUploadingImagem, setIsUploadingImagem] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<ConfiguracaoTermoFormData>({
        resolver: zodResolver(configuracaoTermoSchema),
    });

    // Buscar configuração existente
    const { data: configuracaoData, isLoading, error, refetch } = useQuery({
        queryKey: ['configuracaoTermo'],
        queryFn: async () => {
            const token = localStorage.getItem('token');
            return await configuracaoTermoService.getConfiguracao(String(token));
        },
    });

    useEffect(() => {
        if (error) {
            handleError(error, 'Erro ao carregar configuração');
        }
    }, [error, handleError]);

    // Mutation para criar
    const createMutation = useMutation({
        mutationFn: async (data: ConfiguracaoTermoFormData) => {
            const token = localStorage.getItem('token');
            return await configuracaoTermoService.criar(data, String(token));
        },
        onSuccess: () => {
            handleSuccess('Configuração criada com sucesso!');
            refetch();
        },
        onError: (error: Error) => {
            handleError(error, 'Erro ao criar configuração');
        },
    });

    // Mutation para atualizar
    const updateMutation = useMutation({
        mutationFn: async (data: ConfiguracaoTermoFormData) => {
            const token = localStorage.getItem('token');
            if (!configuracaoData?.id) throw new Error('ID não encontrado');
            return await configuracaoTermoService.atualizar(configuracaoData.id, data, String(token));
        },
        onSuccess: () => {
            handleSuccess('Configuração atualizada com sucesso!');
            refetch();
        },
        onError: (error: Error) => {
            handleError(error, 'Erro ao atualizar configuração');
        },
    });

    // Preencher formulário com dados existentes
    useEffect(() => {
        if (configuracaoData) {
            // Detectar qual tipo de documento está preenchido
            if (configuracaoData.responsavelRg) {
                setTipoDocumento('rg');
                setDocumentoValor(configuracaoData.responsavelRg);
            } else if (configuracaoData.responsavelCpf) {
                setTipoDocumento('cpf');
                setDocumentoValor(configuracaoData.responsavelCpf);
            } else if (configuracaoData.responsavelCnpj) {
                setTipoDocumento('cnpj');
                setDocumentoValor(configuracaoData.responsavelCnpj);
            }

            // Carregar imagem se existir (preview autenticado — HF-5)
            if (configuracaoData.imagemCabecalho) {
                setImagemCabecalho(configuracaoData.imagemCabecalho);
                void imagemService
                    .criarPreviewObjectUrl(configuracaoData.imagemCabecalho)
                    .then((url) => setImagemPreview(url))
                    .catch(() => setImagemPreview(''));
            }

            reset({
                responsavelNome: configuracaoData.responsavelNome || '',
                responsavelRg: configuracaoData.responsavelRg || '',
                responsavelCargo: configuracaoData.responsavelCargo || '',
                responsavelCpf: configuracaoData.responsavelCpf || '',
                responsavelCnpj: configuracaoData.responsavelCnpj || '',
                rodapeEndereco: configuracaoData.rodapeEndereco || '',
                rodapeBairro: configuracaoData.rodapeBairro || '',
                rodapeCidade: configuracaoData.rodapeCidade || '',
                rodapeUf: configuracaoData.rodapeUf || '',
                rodapeCnpj: configuracaoData.rodapeCnpj || '',
                rodapeCep: configuracaoData.rodapeCep || '',
                rodapeTelefone: configuracaoData.rodapeTelefone || '',
                rodapeEmail: configuracaoData.rodapeEmail || '',
                textoPersonalizado: configuracaoData.textoPersonalizado || '',
                imagemCabecalho: configuracaoData.imagemCabecalho || '',
            });
        }
    }, [configuracaoData, reset]);

    // Função para fazer upload da imagem
    const handleImagemUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validar tipo de arquivo
        const tiposPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!tiposPermitidos.includes(file.type)) {
            handleError(new Error('Tipo de arquivo não permitido. Use apenas JPG, PNG, GIF ou WEBP.'), 'Erro ao fazer upload');
            return;
        }

        // Validar tamanho (máximo 5MB)
        const tamanhoMaximo = 5 * 1024 * 1024; // 5MB
        if (file.size > tamanhoMaximo) {
            handleError(new Error('Arquivo muito grande. O tamanho máximo é 5MB.'), 'Erro ao fazer upload');
            return;
        }

        try {
            setIsUploadingImagem(true);
            const resultado = await imagemService.uploadImagem(file);
            
            setImagemCabecalho(resultado.caminhoImagem);
            const previewUrl = await imagemService.criarPreviewObjectUrl(resultado.caminhoImagem);
            setImagemPreview(previewUrl);
            
            handleSuccess('Imagem carregada com sucesso!');
        } catch (error) {
            handleError(error as Error, 'Erro ao fazer upload da imagem');
        } finally {
            setIsUploadingImagem(false);
        }
    };

    // Função para remover a imagem
    const handleRemoverImagem = () => {
        setImagemCabecalho('');
        setImagemPreview('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmit = async (data: ConfiguracaoTermoFormData) => {
        try {
            setIsSubmitting(true);

            // Limpar os campos de documento que não foram selecionados
            const payload = { ...data };
            if (tipoDocumento === 'rg') {
                payload.responsavelRg = documentoValor;
                payload.responsavelCpf = '';
                payload.responsavelCnpj = '';
            } else if (tipoDocumento === 'cpf') {
                payload.responsavelCpf = documentoValor;
                payload.responsavelRg = '';
                payload.responsavelCnpj = '';
            } else if (tipoDocumento === 'cnpj') {
                payload.responsavelCnpj = documentoValor;
                payload.responsavelRg = '';
                payload.responsavelCpf = '';
            }

            // Adicionar caminho da imagem
            payload.imagemCabecalho = imagemCabecalho || '';

            if (configuracaoData) {
                await updateMutation.mutateAsync(payload);
            } else {
                await createMutation.mutateAsync(payload);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Carregando configuração...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Cog6ToothIcon className="h-8 w-8 text-primary-600" />
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Configuração de Termo</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Configure os dados do responsável pela assinatura e rodapé dos termos gerados
                    </p>
                </div>
            </div>

            

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Seção: Responsável pela Assinatura */}
                <Card>
                    <CardHeader>
                        <CardTitle>Responsável pela Assinatura</CardTitle>
                        <CardDescription>
                            Dados do responsável que assinará os termos de estágio
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <Label htmlFor="responsavelNome">Nome Completo</Label>
                                <Input
                                    id="responsavelNome"
                                    placeholder="Ex: Nome completo"
                                    {...register('responsavelNome')}
                                    className={errors.responsavelNome ? 'border-error-500' : ''}
                                />
                                {errors.responsavelNome && (
                                    <p className="mt-1 text-sm text-error-600">{errors.responsavelNome.message}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="tipoDocumento">Tipo de Documento</Label>
                                <select
                                    id="tipoDocumento"
                                    value={tipoDocumento}
                                    onChange={(e) => {
                                        setTipoDocumento(e.target.value as 'rg' | 'cpf' | 'cnpj');
                                        setDocumentoValor(''); // Limpar valor ao trocar tipo
                                    }}
                                    className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="rg">RG</option>
                                    <option value="cpf">CPF</option>
                                    <option value="cnpj">CNPJ</option>
                                </select>
                            </div>

                            <div>
                                <Label htmlFor="documento">
                                    {tipoDocumento === 'rg' && 'RG'}
                                    {tipoDocumento === 'cpf' && 'CPF'}
                                    {tipoDocumento === 'cnpj' && 'CNPJ'}
                                </Label>
                                <Input
                                    id="documento"
                                    placeholder={
                                        tipoDocumento === 'rg' ? 'Ex: 00.000.000-0' :
                                            tipoDocumento === 'cpf' ? 'Ex: 000.000.000-00' :
                                                'Ex: 00.000.000/0000-00'
                                    }
                                    value={documentoValor}
                                    onChange={(e) => setDocumentoValor(e.target.value)}
                                    maxLength={tipoDocumento === 'rg' ? 20 : tipoDocumento === 'cpf' ? 14 : 18}
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <Label htmlFor="responsavelCargo">Cargo</Label>
                                <Input
                                    id="responsavelCargo"
                                    placeholder="Ex: Superintendente da Instituição de nome da cidade"
                                    {...register('responsavelCargo')}
                                    className={errors.responsavelCargo ? 'border-error-500' : ''}
                                />
                                {errors.responsavelCargo && (
                                    <p className="mt-1 text-sm text-error-600">{errors.responsavelCargo.message}</p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Seção: Imagem do Cabeçalho */}
                <Card>
                    <CardHeader>
                        <CardTitle>Imagem do Cabeçalho (PDF)</CardTitle>
                        <CardDescription>
                            Adicione uma imagem que aparecerá centralizada no topo de todas as páginas do PDF
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="imagemCabecalho">Imagem do Cabeçalho (opcional)</Label>
                            <div className="mt-2 space-y-4">
                                {/* Preview da imagem */}
                                {imagemPreview && (
                                    <div className="relative inline-block">
                                        <img
                                            src={imagemPreview}
                                            alt="Preview do cabeçalho"
                                            className="max-w-xs max-h-32 border border-gray-300 rounded-lg shadow-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleRemoverImagem}
                                            className="absolute -top-2 -right-2 bg-error-500 text-white rounded-full p-1 hover:bg-error-600 transition-colors"
                                            title="Remover imagem"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}

                                {/* Botão de upload */}
                                <div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        id="imagemCabecalho"
                                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                        onChange={handleImagemUpload}
                                        className="hidden"
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploadingImagem}
                                        variant="outline"
                                        className="flex items-center gap-2"
                                    >
                                        <PhotoIcon className="h-5 w-5" />
                                        {isUploadingImagem
                                            ? 'Enviando...'
                                            : imagemPreview
                                            ? 'Alterar Imagem'
                                            : 'Selecionar Imagem'
                                        }
                                    </Button>
                                </div>

                                <p className="text-xs text-gray-500">
                                    Formatos aceitos: JPG, PNG, GIF, WEBP | Tamanho máximo: 5MB
                                    <br />
                                    Dimensões recomendadas: 595x86 pixels (será redimensionada automaticamente no PDF)
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Seção: Rodapé do Termo (PDF) */}
                <Card>
                    <CardHeader>
                        <CardTitle>Rodapé do Termo (PDF)</CardTitle>
                        <CardDescription>
                            Informações que aparecerão no rodapé dos termos em PDF
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <Label htmlFor="rodapeEndereco">Endereço</Label>
                                <Input
                                    id="rodapeEndereco"
                                    placeholder="Ex: Rua, número, complemento"
                                    {...register('rodapeEndereco')}
                                />
                            </div>

                            <div>
                                <Label htmlFor="rodapeBairro">Bairro</Label>
                                <Input
                                    id="rodapeBairro"
                                    placeholder="Ex: Bairro"
                                    {...register('rodapeBairro')}
                                />
                            </div>

                            <div>
                                <Label htmlFor="rodapeCidade">Cidade</Label>
                                <Input
                                    id="rodapeCidade"
                                    placeholder="Ex: nome da cidade"
                                    {...register('rodapeCidade')}
                                />
                            </div>

                            <div>
                                <Label htmlFor="rodapeUf">UF</Label>
                                <select
                                    id="rodapeUf"
                                    {...register('rodapeUf')}
                                    className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="">Selecione...</option>
                                    {estadosBrasileiros.map((estado) => (
                                        <option key={estado.sigla} value={estado.sigla}>
                                            {estado.sigla}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <Label htmlFor="rodapeCnpj">CNPJ</Label>
                                <Input
                                    id="rodapeCnpj"
                                    placeholder="Ex: 00.000.000/0000-00"
                                    {...register('rodapeCnpj')}
                                    maxLength={18}
                                />
                            </div>

                            <div>
                                <Label htmlFor="rodapeCep">CEP</Label>
                                <Input
                                    id="rodapeCep"
                                    placeholder="Ex: 00000-000"
                                    {...register('rodapeCep')}
                                    maxLength={10}
                                />
                            </div>

                            <div>
                                <Label htmlFor="rodapeTelefone">Telefone</Label>
                                <Input
                                    id="rodapeTelefone"
                                    placeholder="Ex: (00) 00000-0000"
                                    {...register('rodapeTelefone')}
                                    maxLength={20}
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <Label htmlFor="rodapeEmail">E-mail</Label>
                                <Input
                                    id="rodapeEmail"
                                    type="email"
                                    placeholder="Ex: email@gmail.com"
                                    {...register('rodapeEmail')}
                                    className={errors.rodapeEmail ? 'border-error-500' : ''}
                                />
                                {errors.rodapeEmail && (
                                    <p className="mt-1 text-sm text-error-600">{errors.rodapeEmail.message}</p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Seção: Rodapé Personalizado */}
                <Card>
                    <CardHeader>
                        <CardTitle>Rodapé Personalizado</CardTitle>
                        <CardDescription>
                            Se preenchido, este texto substituirá as informações do rodapé padrão nos PDFs
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div>
                            <Label htmlFor="textoPersonalizado">Texto Personalizado (opcional)</Label>
                            <textarea
                                id="textoPersonalizado"
                                placeholder="Digite aqui um texto personalizado para o rodapé do PDF..."
                                {...register('textoPersonalizado')}
                                rows={4}
                                className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Se este campo estiver vazio, será usado o rodapé padrão com as informações acima.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end">
                    <Button
                        type="submit"
                        disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
                    >
                        {isSubmitting
                            ? 'Salvando...'
                            : (configuracaoData ? 'Atualizar Configuração' : 'Salvar Configuração')
                        }
                    </Button>
                </div>
            </form>

            {/* Footer informativo */}
            <div className="text-center text-sm text-gray-500">
                <p>Todos os campos são opcionais | Preencha apenas os campos necessários</p>
            </div>
        </div>
    );
};

